import type { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';

import {
  clientEngagementSummarySchema,
  createResourceInputSchema,
  requestResourceUploadUrlSchema,
  resourceLocationsBodySchema,
  resourceSchema,
  resourceUploadUrlResponseSchema,
  updateResourceInputSchema,
  type ClientEngagementSummary,
  type CreateResourceInput,
  type PaginatedResources,
  type RequestResourceUploadUrlInput,
  type Resource,
  type ResourceListParams,
  type ResourceLocationsInput,
  type ResourceUploadUrlResponse,
  type UpdateResourceInput,
} from '../types';

/**
 * BE caps `locationIds` at 100 per request for the assign + remove
 * endpoints (elixaa-api/src/schemas/engagementResourceSchema.ts). Chunk
 * any oversized callsite to match. Keep this in sync with the BE max.
 */
const LOCATION_BATCH_SIZE = 100;

function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[engagementResourcesService] ${context} failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({
      status: 0,
      code: 'SHAPE_MISMATCH',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

/**
 * Validate FE-side input before hitting the network. Surfaces the first
 * Zod issue as a user-readable `ApiError(VALIDATION_ERROR)` so call sites
 * can render it without the request actually leaving — saves a round trip
 * and keeps the BE 400 path reserved for genuine cross-cutting failures
 * (BA scope, location membership, publish-twice, etc.).
 */
function validateInputOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first ? `${path ? `${path}: ` : ''}${first.message}` : 'Invalid input';
    throw new ApiError({
      status: 400,
      code: 'VALIDATION_ERROR',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

export const engagementResourcesService = {
  async list(params: ResourceListParams = {}): Promise<PaginatedResources> {
    const { data } = await apiClient.get<{
      data: unknown[];
      pagination?: { page: number; limit: number; total: number };
    }>('/engagement/resources', { params });
    const rows: Resource[] = data.data.map((r) => parseOrThrow(resourceSchema, r, 'list'));
    return {
      data: rows,
      pagination: data.pagination ?? { page: 1, limit: rows.length, total: rows.length },
    };
  },

  async get(id: string): Promise<Resource> {
    const { data } = await apiClient.get<{ data: unknown }>(`/engagement/resources/${id}`);
    return parseOrThrow(resourceSchema, data.data, 'get');
  },

  async create(input: CreateResourceInput): Promise<Resource> {
    const body = validateInputOrThrow(createResourceInputSchema, input, 'create');
    const { data } = await apiClient.post<{ data: unknown }>('/engagement/resources', body);
    return parseOrThrow(resourceSchema, data.data, 'create');
  },

  async update(id: string, input: UpdateResourceInput): Promise<Resource> {
    const body = validateInputOrThrow(updateResourceInputSchema, input, 'update');
    const { data } = await apiClient.patch<{ data: unknown }>(`/engagement/resources/${id}`, body);
    return parseOrThrow(resourceSchema, data.data, 'update');
  },

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/engagement/resources/${id}`);
  },

  async publish(id: string): Promise<void> {
    await apiClient.post(`/engagement/resources/${id}/publish`);
  },

  async archive(id: string): Promise<void> {
    await apiClient.post(`/engagement/resources/${id}/archive`);
  },

  /**
   * Request a presigned S3 upload URL.
   *
   * IMPORTANT: the resource must already exist (BE returns 404 otherwise).
   * The server persists the derived S3 key (`s3ContentKey`/`s3ThumbnailKey`)
   * onto the resource row in the same transaction *before* returning the
   * URL, so the FE never PATCHes the key back. After the subsequent
   * direct `PUT` to `uploadUrl` succeeds, refetch the resource detail to
   * receive the new `contentDownloadUrl` / `thumbnailDownloadUrl`.
   */
  async requestUploadUrl(input: RequestResourceUploadUrlInput): Promise<ResourceUploadUrlResponse> {
    const body = validateInputOrThrow(requestResourceUploadUrlSchema, input, 'requestUploadUrl');
    const { data } = await apiClient.post<{ data: unknown }>(
      '/engagement/resources/upload-url',
      body,
    );
    return parseOrThrow(resourceUploadUrlResponseSchema, data.data, 'requestUploadUrl');
  },

  async assignLocations(id: string, locationIds: string[]): Promise<void> {
    // BE caps `locationIds` at 100 per request (resourceLocationsBodySchema:
    // min(1).max(100)). For large pin operations — multi-org expansion, or
    // any BA with many locations — slice into batches of 100 and dispatch
    // sequentially so the BE never sees an oversized list. Empty lists are
    // a no-op (saves a guaranteed-422 round-trip).
    if (locationIds.length === 0) return;
    for (let i = 0; i < locationIds.length; i += LOCATION_BATCH_SIZE) {
      const chunk = locationIds.slice(i, i + LOCATION_BATCH_SIZE);
      const body = validateInputOrThrow(
        resourceLocationsBodySchema,
        { locationIds: chunk } satisfies ResourceLocationsInput,
        'assignLocations',
      );
      await apiClient.post(`/engagement/resources/${id}/locations`, body);
    }
  },

  async removeLocations(id: string, locationIds: string[]): Promise<void> {
    // Same 100-cap as assign — chunk so "Make BA-wide" / "Remove all" can
    // work on resources currently pinned to more than 100 locations.
    if (locationIds.length === 0) return;
    for (let i = 0; i < locationIds.length; i += LOCATION_BATCH_SIZE) {
      const chunk = locationIds.slice(i, i + LOCATION_BATCH_SIZE);
      const body = validateInputOrThrow(
        resourceLocationsBodySchema,
        { locationIds: chunk } satisfies ResourceLocationsInput,
        'removeLocations',
      );
      await apiClient.delete(`/engagement/resources/${id}/locations`, { data: body });
    }
  },

  async getClientSummary(clientId: string): Promise<ClientEngagementSummary> {
    const { data } = await apiClient.get<{ data: unknown }>(
      `/engagement/clients/${clientId}/summary`,
    );
    return parseOrThrow(clientEngagementSummarySchema, data.data, 'getClientSummary');
  },
};
